import { Component, computed, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { APP_PATHS, SIDEBAR_MENUS, SIDEBAR_MENUS_BOTTOM, XEMEX_OID } from '@core/constants';
import { getUserActivePartner } from '@core/models';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import { PartnershipStore } from '@root/app/store/partnership.store';
import { SideNavComponent, SideNavItem } from './side-nav/side-nav.component';

@Component({
  selector: 'xemex-sidebar',
  imports: [SideNavComponent],
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  readonly userStore = inject(UserStore);
  readonly authStore = inject(AuthStore);
  readonly partnershipStore = inject(PartnershipStore);

  collapsedMenu = input<boolean>(false);

  private isXemex = computed(() => {
    const initialPartner = this.userStore.user().partners.find(p => p.is_initial_partner);
    return initialPartner?.partnerOid == XEMEX_OID;
  });

  /** True when the user may accept partnerships (holds manage_partnerships). */
  private readonly canAcceptPartnerships = computed(() =>
    this.permissions().includes('manage_partnerships'),
  );

  private pendingLoadedForPartner = '';

  constructor() {
    // After login, load the pending partnership invitations the user can accept,
    // so the Partnerships menu item shows a count badge. Re-runs on partner switch.
    effect(() => {
      const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
      if (
        partnerOid &&
        this.canAcceptPartnerships() &&
        partnerOid !== this.pendingLoadedForPartner
      ) {
        this.pendingLoadedForPartner = partnerOid;
        this.partnershipStore.getPendingPartnerships();
      }
    });
  }

  private permissions = computed<string[]>(() => {
    const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    const permissions = this.authStore.permissions();
    return (
      permissions
        ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? []
    );
  });

  topItems = computed<SideNavItem[]>(() => {
    const permissionList = this.permissions();
    const xemex = this.isXemex();
    const pendingPartnerships = this.partnershipStore.pendingCount();
    return SIDEBAR_MENUS.filter(
      m => (!m.permission || permissionList.includes(m.permission)) && (!m.xemexOnly || xemex),
    ).map(m => ({
      icon: m.icon,
      label: m.name,
      link: m.link,
      badge: m.link === APP_PATHS.Partnerships ? pendingPartnerships : undefined,
      badgeTooltip:
        m.link === APP_PATHS.Partnerships ? 'sidebarMenu.pendingPartnershipsTooltip' : undefined,
      section: (m as { section?: string }).section,
    }));
  });

  bottomItems = computed<SideNavItem[]>(() => {
    const xemex = this.isXemex();
    return SIDEBAR_MENUS_BOTTOM.filter(m => !(m as any).xemexOnly || xemex).map(m => ({
      icon: m.icon,
      label: m.name,
      link: (m as any).link,
      externalUrl: (m as any).externalUrl,
    }));
  });
}
